import { isVendedorScopedRole } from "@/types/auth";
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Search, Eye, MapPin, FolderPlus, Save, ExternalLink, Plus } from "lucide-react";
import { useClientesPaginated, useClientesCount, Cliente, type ClientesFiltroParams } from "@/hooks/useClientes";
import { useProjetos } from "@/hooks/useProjetos";
import { useAuth } from "@/hooks/useAuth";
import { useUserNamesByIds } from "@/hooks/useUserNamesByIds";
import { useUsuarios } from "@/hooks/useUsuarios";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { BRASIL_UFS } from "@/lib/estados";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";

export function Clientes() {
  const { user, profile } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [buscaClienteInput, setBuscaClienteInput] = useState(""); // Estado local para o input
  const [buscaCliente, setBuscaCliente] = useState(""); // Estado para a query (com debounce)
  const [sort, setSort] = useState<{ field: 'id' | 'nome' | 'cpf_cnpj' | 'telefone' | 'email' | 'created_at'; direction: 'asc' | 'desc' }>(() => ({
    field: 'id',
    direction: 'desc'
  }));

  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      criadoPorId: "",
      estado: "",
      cidade: "",
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: usuarios = [] } = useUsuarios();
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  const filtrosHook = useMemo<ClientesFiltroParams>(() => {
    const periodo =
      filtrosAplicados.dataInicio || filtrosAplicados.dataFim
        ? { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined }
        : null;
    return {
      periodo,
      criadoPorId: filtrosAplicados.criadoPorId || null,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
    };
  }, [filtrosAplicados]);
  
  // Buscar clientes paginados com busca
  const { data: clientesResult, isLoading, error } = useClientesPaginated(
    currentPage,
    itemsPerPage,
    buscaCliente,
    filtrosHook,
    sort
  );
  const clientes = clientesResult?.data || [];
  const totalClientes = clientesResult?.count || 0;
  const projetosResult = useProjetos();
  const projetos = projetosResult?.data || [];
  const queryClient = useQueryClient();
  
  console.log('🔍 Clientes - Status:', { isLoading, error, clientesCount: clientes?.length });
  
  const [novoCliente, setNovoCliente] = useState({
    nome: "",
    cpf_cnpj: "",
    endereco: "",
    numero: "",
    cep: "",
    cidade: "",
    estado: "",
    bairro: "",
    telefone: "",
    email: ""
  });

  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);
  const [activeTab, setActiveTab] = useState("lista");
  const [editandoCliente, setEditandoCliente] = useState<Cliente | null>(null);
  const [showProjetosModal, setShowProjetosModal] = useState(false);
  const [clienteProjetos, setClienteProjetos] = useState<Cliente | null>(null);

  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (buscaClienteInput === "") {
      setBuscaCliente("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setBuscaCliente(buscaClienteInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [buscaClienteInput]);

  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaCliente, filtrosHook, sort.field, sort.direction]);

  const toggleSort = (field: typeof sort.field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const buscarCep = async (cep: string) => {
    if (cep.replace(/\D/g, '').length !== 8) return;
    const cepLimpo = cep.replace(/\D/g, '');
    let data: { erro?: boolean; logradouro?: string; bairro?: string; localidade?: string; uf?: string } | null = null;

    try {
      const { data: dataFn, error: errFn } = await supabase.functions.invoke('busca-cep', {
        body: { cep: cepLimpo },
      });
      if (!errFn && dataFn && !dataFn.erro) {
        data = dataFn;
      }
    } catch (_) {
      // Ignora erro da Edge Function e tenta ViaCEP direto
    }

    if (!data) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (!json.erro) data = json;
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
        toast.error('Não foi possível buscar o CEP. Verifique sua conexão ou tente novamente.');
        return;
      }
    }

    if (!data || data.erro) {
      toast.error('CEP não encontrado. Verifique e tente novamente.');
      return;
    }

    const preenchido = {
      cep: cepLimpo,
      endereco: data.logradouro || '',
      bairro: data.bairro || '',
      cidade: data.localidade || '',
      estado: data.uf || '',
    };
    if (editandoCliente) {
      setEditandoCliente(prev => (prev ? { ...prev, ...preenchido } : null));
    } else {
      setNovoCliente(prev => ({ ...prev, ...preenchido }));
    }
    toast.success('Endereço preenchido automaticamente.');
  };

  const resetForm = () => {
    setNovoCliente({
      nome: "",
      cpf_cnpj: "",
      endereco: "",
      numero: "",
      cep: "",
      cidade: "",
      estado: "",
      bairro: "",
      telefone: "",
      email: ""
    });
    setEditandoCliente(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const normalizeCpfCnpj = (v: string) => (v || "").replace(/\D/g, "");
      
      if (editandoCliente) {
        // Verificar duplicidade de CPF/CNPJ ao atualizar (exclui o próprio)
        const novoCpfCnpjLimpo = normalizeCpfCnpj(editandoCliente.cpf_cnpj);
        const existeOutro = clientes.some(c => c.id !== editandoCliente.id && normalizeCpfCnpj(c.cpf_cnpj || "") === novoCpfCnpjLimpo);
        if (existeOutro) {
          toast.error("Já existe um cliente cadastrado com este CPF/CNPJ.");
          return;
        }
      } else {
        // Verificar duplicidade de CPF/CNPJ ao criar
        const cpfCnpjLimpo = normalizeCpfCnpj(novoCliente.cpf_cnpj);
        const existe = clientes.some(c => normalizeCpfCnpj(c.cpf_cnpj || "") === cpfCnpjLimpo);
        if (existe) {
          toast.error("Já existe um cliente cadastrado com este CPF/CNPJ.");
          return;
        }
      }
      if (editandoCliente) {
        // Atualizando cliente existente
        console.log('Atualizando cliente:', editandoCliente);
        
        const { data, error } = await supabase
          .from('clientes')
          .update({
            nome: editandoCliente.nome,
            cpf_cnpj: (editandoCliente.cpf_cnpj || "").replace(/\D/g, ""),
            email: editandoCliente.email,
            telefone: editandoCliente.telefone,
            endereco: editandoCliente.endereco,
            numero: editandoCliente.numero,
            bairro: editandoCliente.bairro,
            cidade: editandoCliente.cidade,
            estado: editandoCliente.estado,
            cep: editandoCliente.cep
          })
          .eq('id', editandoCliente.id)
          .select()
          .single();

        if (error) {
          console.error('Erro ao atualizar cliente:', error);
          // Verificar se é erro de duplicidade de CPF/CNPJ
          if (error.code === '23505' || error.message.includes('duplicate') || error.message.includes('unique')) {
            toast.error('Já existe um cliente cadastrado com este CPF/CNPJ.');
          } else {
            toast.error('Erro ao atualizar cliente: ' + error.message);
          }
          return;
        }

        console.log('Cliente atualizado com sucesso:', data);
        toast.success(`Cliente atualizado com sucesso!`);
        
      } else {
        // Criando novo cliente
        console.log('Cadastrando cliente:', novoCliente);
        const insertPayload: Record<string, unknown> = {
          nome: novoCliente.nome,
          cpf_cnpj: (novoCliente.cpf_cnpj || "").replace(/\D/g, ""),
          email: novoCliente.email,
          telefone: novoCliente.telefone,
          endereco: novoCliente.endereco,
          numero: novoCliente.numero,
          bairro: novoCliente.bairro,
          cidade: novoCliente.cidade,
          estado: novoCliente.estado,
          cep: novoCliente.cep
        };
        // Sempre registrar quem criou o cliente (criador = qualquer usuário logado)
        if (user?.id) insertPayload.vendedor_id = user.id;
        const { data, error } = await supabase
          .from('clientes')
          .insert(insertPayload as any)
          .select()
          .single();

        if (error) {
          console.error('Erro ao cadastrar cliente:', error);
          // Verificar se é erro de duplicidade de CPF/CNPJ
          if (error.code === '23505' || error.message.includes('duplicate') || error.message.includes('unique')) {
            toast.error('Já existe um cliente cadastrado com este CPF/CNPJ.');
          } else {
            toast.error('Erro ao cadastrar cliente: ' + error.message);
          }
          return;
        }

        console.log('Cliente cadastrado com sucesso:', data);
        toast.success(`Cliente cadastrado com sucesso! ID: ${data.id}`);
        try {
          localStorage.setItem(
            'ultimoClienteCadastrado',
            JSON.stringify({ id: data.id, nome: data.nome })
          );
        } catch {
          /* ignore quota / private mode */
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      
      // Reset form
      resetForm();
      setActiveTab("lista");
      
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Erro inesperado ao processar cliente');
    }
  };

  // Os clientes já vêm filtrados do backend
  const clientesFiltrados = clientes;

  // Calcular dados da paginação
  const totalItems = totalClientes;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = clientesFiltrados; // Já vem paginado e filtrado do backend

  const { data: userNamesMap } = useUserNamesByIds(currentItems.map((c) => c.vendedor_id));

  const visualizarCliente = (cliente: Cliente) => {
    console.log('Visualizando cliente:', cliente);
    setClienteSelecionado(cliente);
    setActiveTab("detalhes");
  };

  const editarCliente = (cliente: Cliente) => {
    console.log('Editando cliente:', cliente);
    setEditandoCliente({
      id: cliente.id,
      nome: cliente.nome,
      cpf_cnpj: cliente.cpf_cnpj || "",
      endereco: cliente.endereco || "",
      numero: cliente.numero || "",
      cep: cliente.cep || "",
      cidade: cliente.cidade || "",
      estado: cliente.estado || "",
      bairro: cliente.bairro || "",
      telefone: cliente.telefone || "",
      email: cliente.email || "",
      created_at: cliente.created_at,
      updated_at: cliente.updated_at
    });
    setActiveTab("cadastro");
  };

  const cancelarEdicao = () => {
    resetForm();
    setActiveTab("lista");
  };

  const getProjetosDoCliente = (clienteId: number) => {
    const projetosArray = Array.isArray(projetos) ? projetos : [];
    return projetosArray.filter((projeto: any) => projeto.cliente_id === clienteId);
  };

  const criarNovoProjeto = (cliente: Cliente) => {
    // Abrir modal com projetos do cliente
    setClienteProjetos(cliente);
    setShowProjetosModal(true);
  };

  const navegarParaProjeto = (projetoId: number) => {
    // Disparar evento para mudar para a seção de projetos
    const event = new CustomEvent('mudar_secao', { detail: 'projetos' });
    window.dispatchEvent(event);
    
    // Armazenar o projeto específico para abrir
    localStorage.setItem('projetoParaAbrir', projetoId.toString());
    
    // Fechar o modal
    setShowProjetosModal(false);
    setClienteProjetos(null);
    
    toast.success(`Navegando para projeto ID: ${projetoId}`);
  };

  const clienteColumns: ListagemColumn<Cliente & { projetosCount?: number; criadoPor?: string }>[] = [
    { key: "id", label: "ID", value: (c) => c.id },
    { key: "nome", label: "Nome", value: (c) => c.nome },
    { key: "cpf_cnpj", label: "CPF/CNPJ", value: (c) => c.cpf_cnpj ?? "" },
    { key: "telefone", label: "Telefone", value: (c) => c.telefone ?? "" },
    { key: "email", label: "E-mail", value: (c) => c.email ?? "" },
    { key: "localizacao", label: "Estado/Cidade", value: (c) => `${c.estado || "—"} / ${c.cidade || "—"}` },
    { key: "criado_por", label: "Criado por", value: (c) => (c as any).criadoPor ?? "" },
    { key: "projetos", label: "Projetos", value: (c) => (c as any).projetosCount ?? 0 },
  ];
  const [visibleClienteCols, setVisibleClienteCols] = useState<string[]>(clienteColumns.map((c) => c.key));
  const toggleClienteCol = (key: string, nextVisible: boolean) => {
    setVisibleClienteCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando clientes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Erro ao carregar clientes: {error.message}</div>
      </div>
    );
  }

  const clienteAtual = editandoCliente || novoCliente;
  const isEditando = !!editandoCliente;

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setBuscaClienteInput("");
    setBuscaCliente("");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Clientes</h2>
          <p className="text-gray-600">Gerencie seus clientes</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-3">
          <TabsTrigger value="lista">Lista de Clientes</TabsTrigger>
          <TabsTrigger value="cadastro">{isEditando ? 'Editar Cliente' : 'Novo Cliente'}</TabsTrigger>
          <TabsTrigger value="detalhes">Detalhes do Cliente</TabsTrigger>
        </TabsList>

        <TabsContent value="lista" className="space-y-4">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <Search className="h-5 w-5 text-green-600" />
                  Clientes Cadastrados ({totalClientes})
                </CardTitle>
                <div className="flex gap-2">
                  <ListagemAcoes
                    title="Clientes"
                    subtitle={`Ordenação: ${sort.field} ${sort.direction} • Página ${currentPage}/${totalPages}`}
                    rows={currentItems.map((c) => ({
                      ...c,
                      projetosCount: getProjetosDoCliente(c.id).length,
                      criadoPor: c.vendedor_id ? (userNamesMap?.[c.vendedor_id] || "—") : "—",
                    }))}
                    columns={clienteColumns}
                    visibleKeys={visibleClienteCols}
                    onToggleColumn={toggleClienteCol}
                    filenameBase={`clientes_p${currentPage}`}
                    enableExport
                    fetchAllRows={async () => {
                      const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;
                      const pageSize = 1000;
                      let offset = 0;
                      const all: any[] = [];

                      // eslint-disable-next-line no-constant-condition
                      while (true) {
                        let query = supabase.from("clientes").select("*").order(sort.field, { ascending: sort.direction === "asc" });
                        if (isVendedor) query = query.eq("vendedor_id", user!.id);

                        if (filtrosHook?.periodo?.dataInicio) query = query.gte("created_at", `${filtrosHook.periodo.dataInicio}T00:00:00`);
                        if (filtrosHook?.periodo?.dataFim) query = query.lte("created_at", `${filtrosHook.periodo.dataFim}T23:59:59`);
                        if (filtrosHook?.criadoPorId) query = query.eq("vendedor_id", filtrosHook.criadoPorId);
                        if (filtrosHook?.estado) query = query.eq("estado", filtrosHook.estado.toUpperCase());
                        if (filtrosHook?.cidade) query = query.eq("cidade", filtrosHook.cidade);

                        if (buscaCliente && buscaCliente.trim()) {
                          const searchTermTrimmed = buscaCliente.trim();
                          const searchLower = searchTermTrimmed.toLowerCase();
                          const searchNumbersOnly = searchTermTrimmed.replace(/\D/g, "");
                          if (searchNumbersOnly.length > 0 && /^\d+$/.test(searchTermTrimmed)) {
                            if (searchNumbersOnly.length <= 6) {
                              const idSearch = parseInt(searchTermTrimmed);
                              query = query.or(`id.eq.${idSearch},cpf_cnpj.ilike.%${searchNumbersOnly}%`);
                            } else {
                              query = query.ilike("cpf_cnpj", `%${searchNumbersOnly}%`);
                            }
                          } else {
                            query = query.or(
                              `nome.ilike.%${searchLower}%,` +
                                `cpf_cnpj.ilike.%${searchLower}%,` +
                                `email.ilike.%${searchLower}%,` +
                                `telefone.ilike.%${searchLower}%`
                            );
                          }
                        }

                        const { data, error } = await query.range(offset, offset + pageSize - 1);
                        if (error) throw error;
                        const rows = (data || []) as Cliente[];
                        if (rows.length === 0) break;
                        all.push(...rows);
                        if (rows.length < pageSize) break;
                        offset += pageSize;
                      }

                      return all.map((c) => ({
                        ...c,
                        projetosCount: getProjetosDoCliente(c.id).length,
                        criadoPor: c.vendedor_id ? (userNamesMap?.[c.vendedor_id] || "—") : "—",
                      }));
                    }}
                  />
                  <Button 
                    variant="outline"
                    onClick={() => {
                      resetForm();
                      setActiveTab("cadastro");
                    }}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Novo Cliente
                  </Button>
                </div>
              </div>
              <div className="mt-4 rounded-md bg-[#C9C9C9] p-3 text-xs">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
                  <div className="lg:col-span-2">
                    <Label className="text-xs">Buscar</Label>
                    <Input
                      placeholder="ID, nome, CPF/CNPJ, telefone ou e-mail..."
                      className="h-8 text-xs"
                      value={buscaClienteInput}
                      onChange={(e) => setBuscaClienteInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setBuscaCliente(buscaClienteInput);
                        }
                      }}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Período - início</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={filtrosDraft.dataInicio}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataInicio: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Período - fim</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={filtrosDraft.dataFim}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataFim: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Criado por</Label>
                    <Select
                      value={filtrosDraft.criadoPorId || "todos"}
                      onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, criadoPorId: v === "todos" ? "" : v }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {usuarios.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.nome || u.email || u.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Estado</Label>
                    <Select
                      value={filtrosDraft.estado || "todos"}
                      onValueChange={(v) => {
                        const nextEstado = v === "todos" ? "" : v;
                        setFiltrosDraft((p) => ({ ...p, estado: nextEstado, cidade: "" }));
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {BRASIL_UFS.map((uf) => (
                          <SelectItem key={uf.sigla} value={uf.sigla}>
                            {uf.sigla} - {uf.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Cidade</Label>
                    <Select
                      value={filtrosDraft.cidade || "todas"}
                      onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, cidade: v === "todas" ? "" : v }))}
                      disabled={!filtrosDraft.estado}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={filtrosDraft.estado ? "Todas" : "Selecione um estado"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas</SelectItem>
                        {cidadesDoEstado.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button className="h-8 px-3 text-xs" variant="secondary" onClick={handleLimparFiltros}>
                    Limpar
                  </Button>
                  <Button className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90" onClick={handleAplicarFiltros}>
                    Aplicar filtro
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-[11px] leading-tight">
                  <thead>
                    <tr className="border-b">
                      {visibleClienteCols.includes("id") && (
                        <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")} onClick={() => toggleSort('id')}>
                          ID {sort.field === 'id' ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      )}
                      {visibleClienteCols.includes("nome") && (
                        <th className={cn(listagemThLeft, "cursor-pointer select-none")} onClick={() => toggleSort('nome')}>
                          Nome {sort.field === 'nome' ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      )}
                      {visibleClienteCols.includes("cpf_cnpj") && (
                        <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")} onClick={() => toggleSort('cpf_cnpj')}>
                          CPF/CNPJ {sort.field === 'cpf_cnpj' ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      )}
                      {visibleClienteCols.includes("telefone") && (
                        <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")} onClick={() => toggleSort('telefone')}>
                          Telefone {sort.field === 'telefone' ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      )}
                      {visibleClienteCols.includes("email") && (
                        <th className={cn(listagemThLeft, "cursor-pointer select-none hidden lg:table-cell")} onClick={() => toggleSort('email')}>
                          E-mail {sort.field === 'email' ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      )}
                      {visibleClienteCols.includes("localizacao") && (
                        <th className={cn(listagemThLeft, "whitespace-nowrap hidden md:table-cell")}>
                          Estado/Cidade
                        </th>
                      )}
                      {visibleClienteCols.includes("criado_por") && <th className={cn(listagemThLeft, "hidden lg:table-cell")}>Criado por</th>}
                      {visibleClienteCols.includes("projetos") && <th className={cn(listagemThCenter, "whitespace-nowrap hidden md:table-cell")}>Projetos</th>}
                      <th className={cn(listagemThCenter, "whitespace-nowrap")}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-gray-500">
                          {buscaClienteInput ? 'Nenhum cliente encontrado para a busca' : 'Nenhum cliente cadastrado'}
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((cliente) => {
                        const projetosCliente = getProjetosDoCliente(cliente.id);
                        return (
                          <tr key={cliente.id} className="border-b odd:bg-gray-50/60 hover:bg-gray-50">
                            {visibleClienteCols.includes("id") && <td className={cn(listagemTdCenter, "text-gray-900 font-semibold whitespace-nowrap")}>{cliente.id}</td>}
                            {visibleClienteCols.includes("nome") && (
                              <td className={cn(listagemTdLeft, "text-gray-900")}>
                                <div className="max-w-[260px] truncate font-medium">{cliente.nome}</div>
                                <div className="text-[10px] text-gray-500 md:hidden">
                                  {(cliente.estado || "—").trim()} / {(cliente.cidade || "—").trim()}
                                </div>
                              </td>
                            )}
                            {visibleClienteCols.includes("cpf_cnpj") && <td className={cn(listagemTdCenter, "text-gray-600 whitespace-nowrap")}>{cliente.cpf_cnpj}</td>}
                            {visibleClienteCols.includes("telefone") && <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap")}>{cliente.telefone}</td>}
                            {visibleClienteCols.includes("email") && <td className={cn(listagemTdLeft, "text-gray-600 hidden lg:table-cell max-w-[240px] truncate")}>{cliente.email}</td>}
                            {visibleClienteCols.includes("localizacao") && <td className={cn(listagemTdLeft, "text-gray-600 whitespace-nowrap hidden md:table-cell")}>{cliente.estado || "—"} / {cliente.cidade || "—"}</td>}
                            {visibleClienteCols.includes("criado_por") && (
                              <td className={cn(listagemTdCenter, "text-gray-600 hidden lg:table-cell")}>
                                {cliente.vendedor_id ? (userNamesMap?.[cliente.vendedor_id] || "—") : "—"}
                              </td>
                            )}
                            {visibleClienteCols.includes("projetos") && <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap")}>{projetosCliente.length} projeto(s)</td>}
                            <td className={listagemTdCenter}>
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => visualizarCliente(cliente)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => editarCliente(cliente)}
                                >
                                  Editar
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => criarNovoProjeto(cliente)}
                                  className="bg-orange-500 text-white hover:bg-orange-600"
                                >
                                  <FolderPlus className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-gray-700">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cadastro">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  {isEditando ? (
                    <>
                      <Save className="h-5 w-5 text-blue-600" />
                      Editar Cliente
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-5 w-5 text-green-600" />
                      Dados Pessoais
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    value={clienteAtual.nome}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, nome: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, nome: e.target.value});
                      }
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cpf_cnpj">CPF/CNPJ *</Label>
                  <Input
                    id="cpf_cnpj"
                    value={clienteAtual.cpf_cnpj}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, cpf_cnpj: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, cpf_cnpj: e.target.value});
                      }
                    }}
                    placeholder="000.000.000-00 ou 00.000.000/0001-00"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="telefone">Telefone *</Label>
                  <Input
                    id="telefone"
                    value={clienteAtual.telefone}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, telefone: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, telefone: e.target.value});
                      }
                    }}
                    placeholder="(11) 99999-9999"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={clienteAtual.email}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, email: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, email: e.target.value});
                      }
                    }}
                    placeholder="cliente@email.com"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cep">CEP *</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="cep"
                      value={clienteAtual.cep}
                      onChange={(e) => {
                        const cep = e.target.value.replace(/\D/g, '');
                        if (isEditando) {
                          setEditandoCliente(prev => prev ? { ...prev, cep } : null);
                          if (cep.length === 8) buscarCep(cep);
                        } else {
                          setNovoCliente(prev => ({ ...prev, cep }));
                          if (cep.length === 8) buscarCep(cep);
                        }
                      }}
                      onBlur={(e) => {
                        const cep = e.target.value.replace(/\D/g, '');
                        if (cep.length === 8) buscarCep(cep);
                      }}
                      placeholder="00000-000"
                      maxLength={9}
                      required
                    />
                    <Button type="button" variant="outline" size="sm">
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="endereco">Endereço *</Label>
                  <Input
                    id="endereco"
                    value={clienteAtual.endereco}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, endereco: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, endereco: e.target.value});
                      }
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="numero">Número *</Label>
                  <Input
                    id="numero"
                    value={clienteAtual.numero}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, numero: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, numero: e.target.value});
                      }
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bairro">Bairro *</Label>
                  <Input
                    id="bairro"
                    value={clienteAtual.bairro}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, bairro: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, bairro: e.target.value});
                      }
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    value={clienteAtual.cidade}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, cidade: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, cidade: e.target.value});
                      }
                    }}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="estado">Estado *</Label>
                  <Input
                    id="estado"
                    value={clienteAtual.estado}
                    onChange={(e) => {
                      if (isEditando) {
                        setEditandoCliente({...editandoCliente!, estado: e.target.value});
                      } else {
                        setNovoCliente({...novoCliente, estado: e.target.value});
                      }
                    }}
                    placeholder="Ex: SP"
                    maxLength={2}
                    required
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                variant="outline"
                onClick={cancelarEdicao}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className={isEditando ? "bg-blue-600 hover:bg-blue-700 text-white px-8" : "bg-primary hover:bg-primary/90 text-primary-foreground px-8"}
              >
                {isEditando ? 'Salvar Alterações' : 'Cadastrar Cliente'}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="detalhes">
          {clienteSelecionado ? (
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-gray-900">
                  Detalhes do Cliente - {clienteSelecionado.nome}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Dados Pessoais</h3>
                    <div className="space-y-2 text-sm">
                      <div><strong>ID:</strong> {clienteSelecionado.id}</div>
                      <div><strong>Nome:</strong> {clienteSelecionado.nome}</div>
                      <div><strong>CPF/CNPJ:</strong> {clienteSelecionado.cpf_cnpj || 'Não informado'}</div>
                      <div><strong>Endereço:</strong> {clienteSelecionado.endereco || 'Não informado'}</div>
                      <div><strong>Número:</strong> {clienteSelecionado.numero || 'Não informado'}</div>
                      <div><strong>Bairro:</strong> {clienteSelecionado.bairro || 'Não informado'}</div>
                      <div><strong>Cidade:</strong> {clienteSelecionado.cidade || 'Não informado'}</div>
                      <div><strong>Estado:</strong> {clienteSelecionado.estado || 'Não informado'}</div>
                      <div><strong>CEP:</strong> {clienteSelecionado.cep || 'Não informado'}</div>
                      <div><strong>Telefone:</strong> {clienteSelecionado.telefone || 'Não informado'}</div>
                      <div><strong>E-mail:</strong> {clienteSelecionado.email || 'Não informado'}</div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Projetos</h3>
                    <div className="space-y-2 text-sm">
                      {getProjetosDoCliente(clienteSelecionado.id).map((projeto) => (
                        <div key={projeto.id} className="p-2 bg-gray-50 rounded">
                          <div><strong>Status:</strong> {projeto.status}</div>
                          <div><strong>Data:</strong> {new Date(projeto.created_at).toLocaleDateString('pt-BR')}</div>
                        </div>
                      ))}
                      {getProjetosDoCliente(clienteSelecionado.id).length === 0 && (
                        <div className="text-gray-500">Nenhum projeto cadastrado</div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex gap-2">
                  <Button 
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => editarCliente(clienteSelecionado)}
                  >
                    Editar Cliente
                  </Button>
                  <Button 
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => criarNovoProjeto(clienteSelecionado)}
                  >
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Novo Projeto
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="text-center text-gray-500">
                  <p>Selecione um cliente na lista para visualizar os detalhes</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de Projetos do Cliente */}
      <Dialog open={showProjetosModal} onOpenChange={setShowProjetosModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-orange-600" />
              Projetos do Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {clienteProjetos && (
              <div className="text-sm text-gray-600">
                <strong>Cliente:</strong> {clienteProjetos.nome}
              </div>
            )}
            
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Projetos Encontrados:</h4>
              {clienteProjetos && getProjetosDoCliente(clienteProjetos.id).length > 0 ? (
                <div className="space-y-2">
                  {getProjetosDoCliente(clienteProjetos.id).map((projeto) => (
                    <Button
                      key={projeto.id}
                      variant="outline"
                      className="w-full justify-between hover:bg-orange-50 hover:border-orange-300"
                      onClick={() => navegarParaProjeto(projeto.id)}
                    >
                      <span className="font-medium">Projeto ID: {projeto.id}</span>
                      <ExternalLink className="h-4 w-4 text-orange-600" />
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <FolderPlus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>Nenhum projeto encontrado para este cliente</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProjetosModal(false);
                  setClienteProjetos(null);
                }}
              >
                Fechar
              </Button>
              {clienteProjetos && (
                <Button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={() => {
                    // Navegar para criar novo projeto (permite vários por cliente)
                    const event = new CustomEvent('mudar_secao', { detail: 'projetos' });
                    window.dispatchEvent(event);
                    
                    localStorage.setItem('clienteParaProjeto', JSON.stringify(clienteProjetos));
                    
                    setTimeout(() => {
                      const openModalEvent = new CustomEvent('abrir_novo_projeto');
                      window.dispatchEvent(openModalEvent);
                    }, 100);
                    
                    setShowProjetosModal(false);
                    setClienteProjetos(null);
                    
                    toast.success(`Criando novo projeto para: ${clienteProjetos.nome}`);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Projeto
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}